import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CostCodeCreate,
  type CostCodePatch,
  UNIT_LABELS,
  type JobCostCode,
  type Unit,
} from '@siteday/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { GripVertical, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import type { z } from 'zod';

import { errorMessage, jobsApi, qk, quickbooksApi } from '@/api';
import { ErrorState, LoadingState, PageHeader } from '@/components/PageHeader';
import { JobStatusPill } from '@/components/StatusPill';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input, Select } from '@/components/ui/input';
import { Card, CardBody, CardHeader, EmptyRow, Table, Th } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { CheckboxChip } from '@/components/ui/toggle';
import { Tooltip } from '@/components/ui/tooltip';
import { useCompanyBasics } from '@/features/auth/session';
import { useJob, useJobsList } from '@/features/jobs/useJobs';
import { useQboCandidates, useQboStatus } from '@/features/jobs/JobForm';
import { cn } from '@/lib/cn';
import { formatQuantity } from '@/lib/format';

import { daysFromPerDay, planHelper } from './planHelper';

export function useCostCodes(jobId: string) {
  return useQuery({
    queryKey: qk.costCodes.list(jobId),
    queryFn: () => jobsApi.listCostCodes(jobId),
    enabled: !!jobId,
  });
}

function useInvalidateCostCodes(jobId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.costCodes.list(jobId) });
    void qc.invalidateQueries({ queryKey: qk.jobs.detail(jobId) });
    void qc.invalidateQueries({ queryKey: qk.dashboard.all() });
  };
}

/** W6 — inline edit, drag to reorder, unit select from unitsEnabled, plan helper, import/copy, archive. */
export function CostCodesPage() {
  const { id: jobId = '' } = useParams();
  const job = useJob(jobId);
  const codes = useCostCodes(jobId);
  const { unitsEnabled } = useCompanyBasics();
  const qbo = useQboStatus();
  const candidates = useQboCandidates(qbo.data?.connected === true);
  const invalidate = useInvalidateCostCodes(jobId);
  const toast = useToast();
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [copying, setCopying] = useState(false);
  /** Drag order shown optimistically until the server confirms; null means "server order". */
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);

  const items = useMemo(() => codes.data?.items ?? [], [codes.data]);
  const ordered = useMemo(
    () =>
      pendingOrder
        ? pendingOrder
            .map((id) => items.find((c) => c.id === id))
            .filter((c): c is JobCostCode => !!c)
        : items,
    [pendingOrder, items],
  );
  const order = useMemo(() => ordered.map((c) => c.id), [ordered]);

  const patch = useMutation({
    mutationFn: ({ id, data }: { id: string; data: z.input<typeof CostCodePatch> }) =>
      jobsApi.patchCostCode(jobId, id, data),
    onSuccess: invalidate,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const archive = useMutation({
    mutationFn: (id: string) => jobsApi.archiveCostCode(jobId, id),
    onSuccess: (row) => {
      invalidate();
      toast.success(
        row.archived ? `${row.code} archived — foremen no longer see it` : `${row.code} restored`,
      );
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => jobsApi.orderCostCodes(jobId, ids),
    onSuccess: () => {
      invalidate();
      toast.success('Order saved — this is the order foremen see');
    },
    onError: (e) => toast.error(errorMessage(e)),
    onSettled: () => setPendingOrder(null),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const next = arrayMove(order, order.indexOf(String(active.id)), order.indexOf(String(over.id)));
    setPendingOrder(next);
    reorder.mutate(next);
  };

  const units = (
    unitsEnabled.length ? unitsEnabled : (Object.keys(UNIT_LABELS) as Unit[])
  ) as Unit[];
  const selectedCode =
    ordered.find((c) => c.id === selected) ?? ordered.find((c) => !c.archived) ?? null;
  const qboLabel = (c: JobCostCode) => {
    if (!c.qboItemId && !c.qboClassId) return null;
    const item = c.qboItemId
      ? (candidates.data?.items.find((i) => i.id === c.qboItemId)?.name ?? c.qboItemId)
      : null;
    const cls = c.qboClassId
      ? (candidates.data?.classes.find((i) => i.id === c.qboClassId)?.name ?? c.qboClassId)
      : null;
    return [item ? `Item · ${item.replace(/^\S+\s/, '')}` : null, cls ? `Class · ${cls}` : null]
      .filter(Boolean)
      .join(' · ');
  };

  if (job.isPending || codes.isPending) return <LoadingState />;
  if (job.isError) return <ErrorState error={job.error} onRetry={() => void job.refetch()} />;
  if (codes.isError) return <ErrorState error={codes.error} onRetry={() => void codes.refetch()} />;
  const connected = qbo.data?.connected === true;

  return (
    <>
      {/* When reached through the job tabs the header is already on screen; standalone URL gets its own. */}
      {!job.data ? null : (
        <PageHeader
          className="sr-only"
          title={job.data.name}
          sub={
            <>
              {job.data.clientName} · <JobStatusPill status={job.data.status} />
            </>
          }
        />
      )}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-ink3">
          Drag rows to set the order foremen see. Codes with logged quantities can be archived, not
          deleted.
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Tooltip
            text={
              connected
                ? 'Pick items or classes from the last import'
                : 'Connect QuickBooks under the QuickBooks page to import items'
            }
          >
            <Button disabled={!connected} onClick={() => setImporting(true)}>
              Import from QuickBooks
            </Button>
          </Tooltip>
          <Button onClick={() => setCopying(true)}>Copy from job</Button>
          <Button variant="primary" onClick={() => setAdding(true)}>
            <Plus /> Add code
          </Button>
        </div>
      </div>
      <div className="three">
        <Card>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={order} strategy={verticalListSortingStrategy}>
              <Table>
                <thead>
                  <tr>
                    <Th className="w-6" />
                    <Th>Code</Th>
                    <Th>Description</Th>
                    <Th>Unit</Th>
                    <Th right>Planned total</Th>
                    <Th right>Per day</Th>
                    <Th>QuickBooks item / class</Th>
                    <Th>Archived</Th>
                  </tr>
                </thead>
                <tbody>
                  {ordered.length === 0 ? (
                    <EmptyRow colSpan={8}>
                      No cost codes yet. Add one, copy from another job, or import from QuickBooks.
                    </EmptyRow>
                  ) : null}
                  {ordered.map((c) => (
                    <SortableRow
                      key={c.id}
                      code={c}
                      units={units}
                      selected={selectedCode?.id === c.id}
                      qboLabel={qboLabel(c)}
                      onSelect={() => setSelected(c.id)}
                      onPatch={(data) => patch.mutate({ id: c.id, data })}
                      onArchive={() => archive.mutate(c.id)}
                    />
                  ))}
                </tbody>
              </Table>
            </SortableContext>
          </DndContext>
          <div className="border-t border-line2 p-4 text-xs text-ink3">
            Units offered come from Settings → Units enabled. Quantities already logged keep their
            unit.
          </div>
        </Card>
        <Card className="self-start">
          <CardHeader title={selectedCode ? `Plan helper · ${selectedCode.code}` : 'Plan helper'} />
          <CardBody>
            {selectedCode ? (
              <PlanHelper
                key={selectedCode.id}
                code={selectedCode}
                pending={patch.isPending}
                onApply={(plannedTotalQty, plannedQtyPerDay) =>
                  patch.mutate(
                    { id: selectedCode.id, data: { plannedTotalQty, plannedQtyPerDay } },
                    { onSuccess: () => toast.success(`Plan applied to ${selectedCode.code}`) },
                  )
                }
              />
            ) : (
              <p className="text-ink3">Select a code to plan it.</p>
            )}
          </CardBody>
        </Card>
      </div>

      <AddCodeDialog
        open={adding}
        onClose={() => setAdding(false)}
        jobId={jobId}
        units={units}
        onCreated={invalidate}
      />
      {copying ? (
        <CopyFromJobDialog
          open
          jobId={jobId}
          onClose={() => setCopying(false)}
          onDone={invalidate}
        />
      ) : null}
      {importing && connected ? (
        <ImportFromQboDialog
          open
          jobId={jobId}
          units={units}
          existing={ordered}
          onClose={() => setImporting(false)}
          onDone={invalidate}
        />
      ) : null}
    </>
  );
}

function SortableRow({
  code,
  units,
  selected,
  qboLabel,
  onSelect,
  onPatch,
  onArchive,
}: {
  code: JobCostCode;
  units: Unit[];
  selected: boolean;
  qboLabel: string | null;
  onSelect: () => void;
  onPatch: (data: z.input<typeof CostCodePatch>) => void;
  onArchive: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: code.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const muted = code.archived ? 'text-ink3' : '';
  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={cn(selected && 'hi')}
      onClick={onSelect}
      data-testid="cost-code-row"
    >
      <td
        className="w-6 pr-0 text-line"
        {...attributes}
        {...listeners}
        aria-label={`Drag to reorder ${code.code}`}
      >
        <GripVertical className="size-4 cursor-grab" />
      </td>
      <td>
        <InlineText
          value={code.code}
          className={cn('font-cond font-bold', muted)}
          onCommit={(v) => onPatch({ code: v })}
          label={`Code ${code.code}`}
        />
      </td>
      <td>
        <InlineText
          value={code.description}
          className={muted}
          onCommit={(v) => onPatch({ description: v })}
          label={`Description of ${code.code}`}
        />
      </td>
      <td>
        <Select
          aria-label={`Unit of ${code.code}`}
          value={code.unit}
          onChange={(e) => onPatch({ unit: e.target.value as Unit })}
          className="h-[26px] w-auto text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          {(units.includes(code.unit) ? units : [...units, code.unit]).map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </Select>
      </td>
      <td className="r">
        <InlineText
          value={code.plannedTotalQty}
          numeric
          className={muted}
          onCommit={(v) => onPatch({ plannedTotalQty: v || null })}
          label={`Planned total of ${code.code}`}
        />
      </td>
      <td className="r">
        <InlineText
          value={code.plannedQtyPerDay}
          numeric
          className={muted}
          onCommit={(v) => onPatch({ plannedQtyPerDay: v || null })}
          label={`Planned per day of ${code.code}`}
        />
      </td>
      <td className={qboLabel ? undefined : 'text-ink3'}>
        {qboLabel ?? (code.archived ? '—' : 'not mapped')}
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <CheckboxChip
          label={<span className="sr-only">Archived</span>}
          checked={code.archived}
          onChange={onArchive}
          className="h-7 px-1.5"
        />
      </td>
    </tr>
  );
}

/** Dashed-underline inline editor: click to edit, Enter/blur commits, Escape cancels. */
function InlineText({
  value,
  onCommit,
  className,
  numeric,
  label,
}: {
  value: string | null;
  onCommit: (v: string) => void;
  className?: string | undefined;
  numeric?: boolean | undefined;
  label: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  if (editing) {
    return (
      <Input
        autoFocus
        aria-label={label}
        value={draft}
        inputMode={numeric ? 'decimal' : undefined}
        className={cn('h-7 px-1.5 text-[13px]', numeric && 'w-24 text-right')}
        onChange={(e) => setDraft(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onBlur={() => {
          setEditing(false);
          const v = draft.replace(/,/g, '').trim();
          if (v !== (value ?? '')) {
            if (numeric && v && !/^\d+(\.\d+)?$/.test(v)) return;
            onCommit(v);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            setDraft(value ?? '');
            setEditing(false);
          }
        }}
      />
    );
  }
  return (
    <button
      type="button"
      aria-label={`Edit ${label}`}
      className={cn('cursor-text border-b border-dashed border-ink3 text-left num', className)}
      onClick={(e) => {
        e.stopPropagation();
        setDraft(value ?? '');
        setEditing(true);
      }}
    >
      {value === null || value === '' ? '—' : numeric ? formatQuantity(value) : value}
    </button>
  );
}

function PlanHelper({
  code,
  pending,
  onApply,
}: {
  code: JobCostCode;
  pending: boolean;
  onApply: (total: string, perDay: string) => void;
}) {
  const [total, setTotal] = useState(code.plannedTotalQty ?? '');
  const [days, setDays] = useState(() =>
    code.plannedTotalQty && code.plannedQtyPerDay
      ? String(daysFromPerDay(code.plannedTotalQty, code.plannedQtyPerDay) ?? '')
      : '',
  );
  const [perDayOverride, setPerDayOverride] = useState<string | null>(null);
  const computed = planHelper(total, days);
  const perDay = perDayOverride ?? computed.perDay ?? '';

  return (
    <div className="flex flex-col gap-3">
      <Field label="Planned total" htmlFor="ph-total">
        <div className="flex items-center gap-2">
          <Input
            id="ph-total"
            inputMode="decimal"
            value={total}
            onChange={(e) => setTotal(e.target.value)}
          />
          <span className="text-ink3">{code.unit}</span>
        </div>
      </Field>
      <Field label="Working days" htmlFor="ph-days">
        <Input
          id="ph-days"
          inputMode="numeric"
          value={days}
          onChange={(e) => {
            setDays(e.target.value);
            setPerDayOverride(null);
          }}
        />
      </Field>
      <Field
        label="Planned per day"
        htmlFor="ph-perday"
        hint="Filled from total ÷ days. Edit either side."
      >
        <div className="flex items-center gap-2">
          <Input
            id="ph-perday"
            inputMode="decimal"
            className="bg-surface2 font-bold"
            value={perDay}
            onChange={(e) => {
              setPerDayOverride(e.target.value);
              const d = daysFromPerDay(total, e.target.value);
              if (d !== null) setDays(String(d));
            }}
          />
          <span className="whitespace-nowrap text-ink3">{code.unit} / day</span>
        </div>
      </Field>
      <Button
        variant="primary"
        className="w-full"
        disabled={!perDay || !total}
        loading={pending}
        onClick={() => onApply(total.replace(/,/g, ''), perDay.replace(/,/g, ''))}
      >
        Apply to {code.code}
      </Button>
    </div>
  );
}

function AddCodeDialog({
  open,
  onClose,
  jobId,
  units,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  jobId: string;
  units: Unit[];
  onCreated: () => void;
}) {
  const toast = useToast();
  const form = useForm<z.input<typeof CostCodeCreate>, unknown, z.output<typeof CostCodeCreate>>({
    resolver: zodResolver(CostCodeCreate),
    defaultValues: {
      code: '',
      description: '',
      unit: units[0] ?? 'LF',
      plannedTotalQty: null,
      plannedQtyPerDay: null,
    },
  });
  const create = useMutation({
    mutationFn: (v: z.output<typeof CostCodeCreate>) => jobsApi.createCostCode(jobId, v),
    onSuccess: (row) => {
      onCreated();
      toast.success(`${row.code} added`);
      form.reset();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const err = form.formState.errors;
  const numeric = (v: string | null | undefined) => (v === '' || v === undefined ? null : v);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add cost code"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="add-code-form" loading={create.isPending}>
            Add code
          </Button>
        </>
      }
    >
      <form
        id="add-code-form"
        noValidate
        className="form-grid"
        onSubmit={form.handleSubmit((v) =>
          create.mutate({
            ...v,
            plannedTotalQty: numeric(v.plannedTotalQty),
            plannedQtyPerDay: numeric(v.plannedQtyPerDay),
          }),
        )}
      >
        <Field label="Code" htmlFor="cc-code" error={err.code?.message}>
          <Input
            id="cc-code"
            autoFocus
            placeholder="02-300"
            invalid={!!err.code}
            {...form.register('code')}
          />
        </Field>
        <Field label="Unit" htmlFor="cc-unit" error={err.unit?.message}>
          <Select id="cc-unit" {...form.register('unit')}>
            {units.map((u) => (
              <option key={u} value={u}>
                {u} · {UNIT_LABELS[u]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Description" htmlFor="cc-desc" error={err.description?.message} wide>
          <Input
            id="cc-desc"
            placeholder='Storm pipe 12" RCP'
            invalid={!!err.description}
            {...form.register('description')}
          />
        </Field>
        <Field label="Planned total" htmlFor="cc-total" error={err.plannedTotalQty?.message}>
          <Input
            id="cc-total"
            inputMode="decimal"
            placeholder="2400"
            {...form.register('plannedTotalQty')}
          />
        </Field>
        <Field label="Planned per day" htmlFor="cc-perday" error={err.plannedQtyPerDay?.message}>
          <Input
            id="cc-perday"
            inputMode="decimal"
            placeholder="200"
            {...form.register('plannedQtyPerDay')}
          />
        </Field>
      </form>
    </Dialog>
  );
}

function CopyFromJobDialog({
  open,
  jobId,
  onClose,
  onDone,
}: {
  open: boolean;
  jobId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const jobs = useJobsList({});
  const [source, setSource] = useState('');
  const toast = useToast();
  const copy = useMutation({
    mutationFn: () => jobsApi.copyCostCodesFrom(jobId, source),
    onSuccess: (res) => {
      onDone();
      toast.success(`Codes copied — ${res.items.length} on this job now`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Copy cost codes from another job"
      description="Copies codes and their plans. Codes that already exist here are skipped."
      width="sm"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!source}
            loading={copy.isPending}
            onClick={() => copy.mutate()}
          >
            Copy codes
          </Button>
        </>
      }
    >
      <Field label="Source job" htmlFor="copy-source">
        <Select id="copy-source" value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">Pick a job…</option>
          {(jobs.data?.items ?? [])
            .filter((j) => j.id !== jobId)
            .map((j) => (
              <option key={j.id} value={j.id}>
                {j.name} ({j.costCodes?.length ?? 0} codes)
              </option>
            ))}
        </Select>
      </Field>
    </Dialog>
  );
}

function ImportFromQboDialog({
  open,
  jobId,
  units,
  existing,
  onClose,
  onDone,
}: {
  open: boolean;
  jobId: string;
  units: Unit[];
  existing: JobCostCode[];
  onClose: () => void;
  onDone: () => void;
}) {
  const candidates = useQuery({
    queryKey: qk.quickbooks.candidates(),
    queryFn: quickbooksApi.getCandidates,
  });
  const [picked, setPicked] = useState<Record<string, Unit>>({});
  const toast = useToast();
  const existingItems = new Set(existing.map((c) => c.qboItemId));
  const run = useMutation({
    mutationFn: async () => {
      let n = 0;
      for (const [itemId, unit] of Object.entries(picked)) {
        const item = candidates.data?.items.find((i) => i.id === itemId);
        if (!item) continue;
        const m = /^(\S+)\s+(.+)$/.exec(item.name);
        await jobsApi.createCostCode(jobId, {
          code: m?.[1] ?? item.name.slice(0, 12),
          description: m?.[2] ?? item.name,
          unit,
          qboItemId: itemId,
        });
        n++;
      }
      return n;
    },
    onSuccess: (n) => {
      onDone();
      toast.success(`${n} codes imported with QuickBooks mapping`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const items = (candidates.data?.items ?? []).filter(
    (i) => !i.name.startsWith('Equipment') && !existingItems.has(i.id),
  );
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Import from QuickBooks"
      description="Service items from the last import. Each becomes a cost code mapped to that item."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={Object.keys(picked).length === 0}
            loading={run.isPending}
            onClick={() => run.mutate()}
          >
            Import {Object.keys(picked).length || ''} codes
          </Button>
        </>
      }
    >
      <Table>
        <thead>
          <tr>
            <Th />
            <Th>QuickBooks item</Th>
            <Th>Unit</Th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <EmptyRow colSpan={3}>Every item is already on this job.</EmptyRow>
          ) : null}
          {items.map((i) => (
            <tr key={i.id}>
              <td>
                <input
                  type="checkbox"
                  aria-label={`Import ${i.name}`}
                  checked={i.id in picked}
                  onChange={(e) =>
                    setPicked((p) => {
                      const next = { ...p };
                      if (e.target.checked) next[i.id] = units[0] ?? 'LF';
                      else delete next[i.id];
                      return next;
                    })
                  }
                />
              </td>
              <td>{i.name}</td>
              <td>
                <Select
                  aria-label={`Unit for ${i.name}`}
                  value={picked[i.id] ?? units[0]}
                  disabled={!(i.id in picked)}
                  onChange={(e) => setPicked((p) => ({ ...p, [i.id]: e.target.value as Unit }))}
                  className="h-7 w-auto text-xs"
                >
                  {units.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </Select>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Dialog>
  );
}
